import React, { useState } from 'react'
import {
  ChevronDown,
  Clock,
  PackageCheck,
  Undo2,
  History,
  MoreHorizontal,
  Warehouse,
  Store,
  ArrowRight,
  User,
  CheckCircle
} from 'lucide-react'
import type { Reservation } from '../../types'

interface LocationOption {
  id: string
  name: string
}

interface InhouseSecondaryActionsProps {
  activeReservationCount: number
  pendingTransferCount: number
  transferHistory: Reservation[]
  locations: LocationOption[]
  showTransferHistory: boolean
  onToggleTransferHistory: () => void
  onOpenReservations: () => void
  onOpenReceiveTransfer: () => void
  onOpenReturnStock: () => void
  children?: React.ReactNode
}

export function InhouseSecondaryActions({
  activeReservationCount,
  pendingTransferCount,
  transferHistory,
  locations,
  showTransferHistory,
  onToggleTransferHistory,
  onOpenReservations,
  onOpenReceiveTransfer,
  onOpenReturnStock,
  children
}: InhouseSecondaryActionsProps) {
  const [expanded, setExpanded] = useState(false)

  const hasAnyAction =
    activeReservationCount > 0 ||
    pendingTransferCount > 0 ||
    transferHistory.length > 0 ||
    !!children

  if (!hasAnyAction) return null

  return (
    <div className="mb-4 border-b border-gray-100 dark:border-gray-700/50 pb-4">
      <button
        type="button"
        onClick={() => setExpanded(prev => !prev)}
        className="w-full flex items-center justify-between py-2 text-sm font-medium text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-gray-200"
        aria-expanded={expanded}
      >
        <span className="inline-flex items-center gap-2">
          <MoreHorizontal className="h-4 w-4" />
          More actions
        </span>
        <ChevronDown className={`h-4 w-4 transition-transform ${expanded ? 'rotate-180' : ''}`} />
      </button>

      {expanded && (
        <div className="mt-2 space-y-2">
          {children}

          <div className="flex flex-wrap gap-2">
            {activeReservationCount > 0 && (
              <button
                type="button"
                onClick={onOpenReservations}
                className="inline-flex items-center gap-1.5 px-3 py-2 text-xs font-medium rounded-lg border border-teal-200 dark:border-teal-700 text-teal-700 dark:text-teal-300 hover:bg-teal-50 dark:hover:bg-teal-900/20"
              >
                <Clock className="h-3.5 w-3.5" />
                Reservations ({activeReservationCount})
              </button>
            )}
            {pendingTransferCount > 0 && (
              <button
                type="button"
                onClick={onOpenReceiveTransfer}
                className="inline-flex items-center gap-1.5 px-3 py-2 text-xs font-medium rounded-lg border border-blue-200 dark:border-blue-700 text-blue-700 dark:text-blue-300 hover:bg-blue-50 dark:hover:bg-blue-900/20"
              >
                <PackageCheck className="h-3.5 w-3.5" />
                Receive ({pendingTransferCount})
              </button>
            )}
            <button
              type="button"
              onClick={onOpenReturnStock}
              className="inline-flex items-center gap-1.5 px-3 py-2 text-xs font-medium rounded-lg border border-amber-200 dark:border-amber-700 text-amber-700 dark:text-amber-300 hover:bg-amber-50 dark:hover:bg-amber-900/20"
            >
              <Undo2 className="h-3.5 w-3.5" />
              Return stock
            </button>
          </div>

          {transferHistory.length > 0 && (
            <div>
              <button
                type="button"
                onClick={onToggleTransferHistory}
                className="w-full flex items-center justify-between p-2 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-800/50 text-sm text-gray-700 dark:text-gray-300"
              >
                <span className="inline-flex items-center gap-2">
                  <History className="h-4 w-4" />
                  Recent transfers ({transferHistory.length})
                </span>
                <ChevronDown className={`h-4 w-4 transition-transform ${showTransferHistory ? 'rotate-180' : ''}`} />
              </button>
              {showTransferHistory && (
                <div className="mt-2 space-y-2 max-h-48 overflow-y-auto">
                  {transferHistory
                    .sort((a, b) => new Date(b.reserved_at || b.created_at).getTime() - new Date(a.reserved_at || a.created_at).getTime())
                    .slice(0, 10)
                    .map(transfer => {
                      const sourceName = locations.find(l => l.id === transfer.source_location_id)?.name || '?'
                      const destName = locations.find(l => l.id === transfer.destination_location_id)?.name || '?'
                      const totalQty = transfer.reservation_items?.reduce((sum, i) => sum + i.quantity, 0) || 0
                      const receivedQty = transfer.reservation_items?.reduce((sum, i) => sum + (i.received_quantity || 0), 0) || 0

                      return (
                        <div key={transfer.id} className="p-2 rounded-lg border border-gray-200 dark:border-gray-700 text-xs">
                          <div className="flex items-center justify-between mb-1">
                            <span className="font-mono font-semibold text-gray-600 dark:text-gray-400">
                              {transfer.reservation_number}
                            </span>
                            <span className="text-gray-400">
                              {receivedQty}/{totalQty}
                            </span>
                          </div>
                          <div className="flex items-center gap-1 text-gray-500">
                            <Warehouse className="h-3 w-3" />
                            {sourceName}
                            <ArrowRight className="h-3 w-3" />
                            <Store className="h-3 w-3" />
                            {destName}
                          </div>
                          {transfer.worker_name && (
                            <p className="mt-1 text-gray-400 flex items-center gap-1">
                              <User className="h-3 w-3" />
                              {transfer.worker_name}
                            </p>
                          )}
                          {transfer.received_at && (
                            <p className="mt-1 text-green-600 dark:text-green-400 flex items-center gap-1">
                              <CheckCircle className="h-3 w-3" />
                              Received
                            </p>
                          )}
                        </div>
                      )
                    })}
                </div>
              )}
            </div>
          )}
        </div>
      )}
    </div>
  )
}
